import React, { useEffect, useState, useCallback } from "react";
import { 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Tooltip,
  TextField,
  InputAdornment,
  Chip
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OrpenService from "../../../core/http/service/OrpenService";
import Colors from "../../../constants/Colors";
import moment from "moment";
import { debounce } from "lodash";

interface Props {
  empresaId: string;
}

const OrpenLogs = ({ empresaId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);

  const fetchLogs = async (p: number, rpp: number, s: string) => {
    setLoading(true);
    try {
      const response: any = await OrpenService.getLogs(empresaId, p + 1, rpp, s);
      if (response.data && response.data.data) {
        setLogs(response.data.data);
        setTotal(response.data.total || response.data.data.length);
      }
    } catch (error) {
      console.error("Error fetching Orpen logs", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(
    debounce((p, rpp, s) => fetchLogs(p, rpp, s), 500),
    [empresaId]
  );

  useEffect(() => {
    debouncedFetch(page, rowsPerPage, search);
  }, [empresaId, page, rowsPerPage, search]);

  const handleOpenDetail = (log: any) => {
    setSelectedLog(log);
    setOpenDetail(true);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedLog(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  if (loading && logs.length === 0) return <CircularProgress />;

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          size="small"
          placeholder="Buscar por paciente ou telefone..."
          value={search}
          onChange={handleSearchChange}
          sx={{ width: 350 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader aria-label="sticky table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Envio</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Consulta</TableCell>
                <TableCell>Destinatário</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log: any) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={log.id}>
                  <TableCell>{moment(log.dataInclusao).format("DD/MM/YY HH:mm")}</TableCell>
                  <TableCell sx={{ fontWeight: '500' }}>{log.pacienteNome || "-"}</TableCell>
                  <TableCell>
                    {log.dataHoraAgendamento ? moment(log.dataHoraAgendamento).format("DD/MM/YY HH:mm") : "-"}
                  </TableCell>
                  <TableCell>{log.destiny}</TableCell>
                  <TableCell>
                    <Chip 
                      label={log.status.toLowerCase() === 'success' ? "Sucesso" : "Erro"} 
                      color={log.status.toLowerCase() === 'success' ? "success" : "error"} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalhes">
                        <IconButton size="small" onClick={() => handleOpenDetail(log)}>
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && !loading && (
                  <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          Nenhum log encontrado para esta busca.
                      </TableCell>
                  </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página"
        />
      </Paper>

      <Dialog open={openDetail} onClose={handleCloseDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: Colors.SPM50, borderBottom: `1px solid ${Colors.SPM100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Detalhes do Envio</Typography>
          <Box sx={{ 
              px: 1.5, 
              py: 0.5, 
              borderRadius: 1, 
              bgcolor: selectedLog?.status === 'success' ? Colors.success : Colors.cancel,
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold'
          }}>
              {selectedLog?.status.toUpperCase()}
          </Box>
        </DialogTitle>
        <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Paciente</Typography>
                        <Typography variant="body2" sx={{ fontWeight: '500' }}>{selectedLog?.pacienteNome || "N/A"}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Destinatário (WhatsApp)</Typography>
                        <Typography variant="body2">{selectedLog?.destiny}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Data da Consulta</Typography>
                        <Typography variant="body2">
                            {selectedLog?.dataHoraAgendamento ? moment(selectedLog.dataHoraAgendamento).format("DD/MM/YYYY [às] HH:mm") : "N/A"}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Data do Envio</Typography>
                        <Typography variant="body2">
                            {selectedLog?.dataInclusao ? moment(selectedLog.dataInclusao).format("DD/MM/YYYY [às] HH:mm:ss") : "N/A"}
                        </Typography>
                    </Box>
                </Box>
                
                <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Mensagem Enviada</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f8f9fa' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedLog?.messageSent}</Typography>
                    </Paper>
                </Box>

                {selectedLog?.errorMessage && selectedLog?.status.toLowerCase() !== 'success' && (
                    <Box>
                        <Typography variant="subtitle2" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ErrorOutlineIcon fontSize="small" /> Erro Retornado
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff5f5', borderColor: '#feb2b2' }}>
                            <Typography variant="body2" color="error" sx={{ fontWeight: '500', wordBreak: 'break-word' }}>
                                {selectedLog?.errorMessage}
                            </Typography>
                        </Paper>
                    </Box>
                )}

                <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Comando CURL (Debug)</Typography>
                    <Paper 
                        variant="outlined" 
                        sx={{ 
                            p: 1.5, 
                            bgcolor: '#2d3748', 
                            color: '#a0aec0', 
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            overflowX: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            border: 'none'
                        }}
                    >
                        {selectedLog?.requestBody || "CURL não disponível."}
                    </Paper>
                </Box>

                <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Resposta Bruta da API</Typography>
                    <Paper 
                        variant="outlined" 
                        sx={{ 
                            p: 1.5, 
                            bgcolor: '#f1f5f9', 
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            overflowX: 'auto',
                            maxHeight: 200
                        }}
                    >
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {selectedLog?.responsePayload ? JSON.stringify(JSON.parse(selectedLog.responsePayload), null, 2) : "Sem payload."}
                        </pre>
                    </Paper>
                </Box>
            </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrpenLogs;
